import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { emitAgentSetup } from '../../../api/createAppSocket';
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { ListAllRoomsApi } from "../../../store/features/ListAllRooms";
import { SearchChatRoomsApi } from "../../../store/features/searchChatRooms";
import { getSoundSetting } from "../../../store/features/getSoundSetting";
import "./style.css";
import ChatUser from "./ChatUser";
import ArchiveUser from "./ArchiveUser";
import UnAssignedRooms from "./UnAssignedRooms";
import { ReactComponent as Jacob } from "../../../assets/images/ChatImages/albert.svg";
import { ReactComponent as Laptop } from "../../../assets/images/ChatImages/laptop.svg";
import Loader from "../../../assets/Loader";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ReactComponent as trustchatLogo } from "../../../assets/images/trustchatlogo.svg";
import { msgTime } from "../../../utils/index";
import { LIVE_CHAT_HOME_PATH } from "../../../config/featureFlags";
import { useSocket } from "../../../SocketContext";
import ChatListSkeleton from "./ChatListSkeleton";
import { message } from "antd";
import {
  getActiveChatListWarmCache,
  setActiveChatListWarmCache,
} from "../../../utils/activeChatListWarmCache";
import {
  getUnassignedChatListWarmCache,
  setUnassignedChatListWarmCache,
  removeRoomFromUnassignedWarmCache,
} from "../../../utils/unassignedChatListWarmCache";
import {
  getArchiveChatListWarmCache,
  setArchiveChatListWarmCache,
} from "../../../utils/archiveChatListWarmCache";
import { ListArchivedRoomsApi } from "../../../store/features/ListArchivedRooms";
import {
  socketPayloadIsUnassignedChat,
  transformSocketRoomToUnassignedListRow,
} from "../../../utils/unassignedSocketRoom";
import { resolveSocketRoomId } from "../../../utils/socketRoomId";
import useUnknownRoomListRefresh from "../../../hooks/useUnknownRoomListRefresh";
import {
  getChatListReadReceiptFromMessage,
  getChatListReadReceiptFromRoom,
} from "../../../utils/chatListReadReceipt";
import {
  getPendingRoomPreview,
  rememberPendingRoomPreview,
  ROOM_PREVIEW_RESOLVED_EVENT,
} from "../../../utils/pendingRoomPreview";
import { isJoinNoticeMessage } from "../../../utils/messageNotification";

/** Every way a chat can stop being active: agent closes it, or the visitor ends it. */
const CHAT_CLOSED_SOCKET_EVENTS = ["archive-chat", "end-room", "end-chat"];

function businessIdsMatch(cachedId, currentId) {
  if (cachedId == null || currentId == null) return false;
  return String(cachedId) === String(currentId);
}

/** Prefer API/socket_active on refetch; keep activity only while still live. */
function mergeVisitorOnlineState(fresh, previous) {
  if (!previous) return fresh;
  const ended = !!(fresh.end_session || previous.end_session);
  if (ended) {
    return {
      ...fresh,
      end_session: true,
      isLive: false,
      isOnline: false,
    };
  }

  const nextLive = typeof fresh.isLive === "boolean" ? !!fresh.isLive : !!previous.isLive;
  return {
    ...fresh,
    end_session: false,
    isLive: nextLive,
    isOnline: nextLive ? !!previous.isOnline || !!fresh.isOnline : false,
  };
}

function readWarmActiveListSeed(businessId) {
  const wc = getActiveChatListWarmCache();
  const warm =
    Boolean(businessId) &&
    wc.hasFetched &&
    businessIdsMatch(wc.businessId, businessId) &&
    Array.isArray(wc.data);
  if (!warm) {
    return { data: [], isLoading: true, assignedCount: 0, hasMore: true };
  }
  return {
    data: wc.data,
    isLoading: false,
    assignedCount: wc.assignedCount ?? wc.data.length,
    hasMore: wc.hasMore !== false,
  };
}

function ChatBar(props) {
  const userData = useSelector((state) => state.loginToken);
  const businessId = userData?.response?.user?.business_id;
  const listSeed = readWarmActiveListSeed(businessId);

  const [data, setData] = useState(listSeed.data);
  const [isLoading, setIsLoading] = useState(listSeed.isLoading);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(listSeed.hasMore);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const pageRef = useRef(page);
  const hasMoreRef = useRef(hasMore);

  const [searchData, setSearchData] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [searchFetchingMore, setSearchFetchingMore] = useState(false);
  const searchFetchingMoreRef = useRef(false);
  const searchPageRef = useRef(1);
  const searchHasMoreRef = useRef(false);
  const searchRequestIdRef = useRef(0);

  const [activeChat, setActiveChat] = useState("Chats");
  const [assignedCount, setAssignedCount] = useState(listSeed.assignedCount);
  const [unAssignedCount, setUnAssignedCount] = useState(0);
  const [closedCount, setClosedCount] = useState(() => {
    const wc = getArchiveChatListWarmCache();
    return typeof wc.total === "number" ? wc.total : 0;
  });
  const [closedRoomSignal, setClosedRoomSignal] = useState(null);

  const [soundOn, setSoundOn] = useState(false);

  const [rmId, setRmId] = useState(null);
  const [typingRooms, setTypingRooms] = useState({});
  const soundOnRef = useRef(soundOn);
  const notificationSound = useRef(
    new Audio("/sound/notification.mp3")
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const socket = useSocket();

  const fromSearch =
    searchParams.get("fromSearch") === "1" ||
    searchParams.get("fromSearch") === "true";
  const urlRoomId = searchParams.get("roomId") || "";
  const filterListForSearchOpen = (items) => {
    if (!fromSearch || !urlRoomId) return items;
    return items.filter((item) => item.id === urlRoomId);
  };

  const {
    setLatestMsgTime,
    selectUser,
    setSelectUser,
    setGetRoomId,
    getRoomId,
    refreshChatList,
    openRoom,
    setAssignedAgentImage,
    typingIndicator,
    setTypingIndicator
    ,
    setChatSideBar,
    isRoomOpen,
    setRoomMessagesLoading,
    variant,
    controlledView,
    onViewChange,
    onCountsChange,
    basePath = LIVE_CHAT_HOME_PATH,
    removeRoomId,
    onRemoveRoomConsumed,
    assignedRoomId,
    onAssignedRoomConsumed,
    assignedRoomClaimForSelf = true,
    searchQuery = "",
  } = props;
  const trimmedSearch = String(searchQuery || "").trim();
  const isSearching = Boolean(trimmedSearch);
  const handleViewChange = (newView, { keepOpenRoom = false } = {}) => {
    if (typeof onViewChange === "function") {
      onViewChange(newView);
    }
    // Keeping `roomId` leaves the open conversation (and its details panel) in place
    // while the list underneath switches tabs.
    const openRoomId = keepOpenRoom ? selectUserRef.current?.id : null;
    const roomParam = openRoomId ? `&roomId=${openRoomId}` : "";
    if (newView === "UnAssigned") {
      navigate(`${basePath}?tab=unassigned${roomParam}`);
    } else if (newView === "Archive") {
      navigate(`${basePath}?tab=closed${roomParam}`);
    } else if (openRoomId) {
      navigate(`${basePath}?roomId=${openRoomId}`);
    } else {
      navigate(basePath);
    }
    setActiveChat(newView);
  };

  console.log(getRoomId, "dsdsdsdsdsdsd");

  console.log(selectUser, "selectUser in ChatBar");
  // Prefer controlled V2 filter for which list is mounted so tab switches don't
  // keep the previous list alive for one effect-cycle (avoids auto-open races).
  const listView = controlledView || activeChat;
  const rmIdRef = useRef(rmId);
  const selectUserRef = useRef(selectUser);
  const isRoomOpenRef = useRef(isRoomOpen);
  const dataRef = useRef(data);
  dataRef.current = data;
  /** Guards the Closed badge against double counting when the same close arrives twice. */
  const countedClosedRoomsRef = useRef(new Set());
  /** Guards Unassigned → Assigned badge moves when Start Chat / assign fires twice. */
  const countedAssignedFromUnassignedRef = useRef(new Set());
  /**
   * Visitor presence learned from sockets, keyed by customer id. The room-list
   * API can answer with a stale `socket_active`, so socket truth wins on refetch.
   */
  const livePresenceRef = useRef(new Map());
  const getRoomListRef = useRef(null);
  const scheduleActiveListRefresh = useUnknownRoomListRefresh({
    refetch: () => getRoomListRef.current?.(1, { background: true }),
    shouldSkip: (roomId) => countedClosedRoomsRef.current.has(roomId),
  });
  const prevRefreshChatListRef = useRef(refreshChatList);
  useEffect(() => {
    notificationSound.current.load();
  }, []);

  // Single source of truth for the visible tab: the controlled V2 view, else `tab`.
  useEffect(() => {
    if (controlledView) {
      setActiveChat(controlledView);
      return;
    }
    const tab = (searchParams.get("tab") || "").toLowerCase();
    if (tab === "unassigned") {
      setActiveChat("UnAssigned");
    } else if (tab === "archive" || tab === "closed") {
      setActiveChat("Archive");
    } else {
      setActiveChat("Chats");
    }
  }, [searchParams, controlledView, openRoom]);

  const prevActiveChatRef = useRef(activeChat);
  useEffect(() => {
    const previousView = prevActiveChatRef.current;
    prevActiveChatRef.current = activeChat;
    if (activeChat !== "Chats" || previousView === "Chats") return;
    void getRoomListRef.current?.(1, { background: true });
  }, [activeChat]);

  useEffect(() => {
    if (typeof onCountsChange !== "function") return;
    onCountsChange({
      assigned_to_me: assignedCount || 0,
      unassigned: unAssignedCount || 0,
      closed: closedCount || 0,
      all: (assignedCount || 0) + (unAssignedCount || 0) + (closedCount || 0),
    });
  }, [assignedCount, unAssignedCount, closedCount, onCountsChange]);
  useEffect(() => {
    rmIdRef.current = rmId;
  }, [rmId]);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    soundOnRef.current = soundOn;
  }, [soundOn]);

  useEffect(() => {
    selectUserRef.current = selectUser;
  }, [selectUser]);

  useEffect(() => {
    isRoomOpenRef.current = isRoomOpen;
  }, [isRoomOpen]);

  useLayoutEffect(() => {
    if (!businessId) return;
    const wc = getUnassignedChatListWarmCache();
    if (
      wc.hasFetched &&
      businessIdsMatch(wc.businessId, businessId) &&
      typeof wc.total === "number"
    ) {
      setUnAssignedCount(wc.total);
    }
    const archiveWc = getArchiveChatListWarmCache();
    if (
      businessIdsMatch(archiveWc.businessId, businessId) &&
      typeof archiveWc.total === "number"
    ) {
      setClosedCount(archiveWc.total);
    }
  }, [businessId]);

  useEffect(() => {
    const uid = userData?.response?.user?._id;
    if (!businessId || !uid) return;
    const wc = getUnassignedChatListWarmCache();
    if (
      wc.hasFetched &&
      businessIdsMatch(wc.businessId, businessId) &&
      typeof wc.total === "number"
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      const result = await dispatch(
        ListAllRoomsApi({
          mainUserId: uid,
          business_id: businessId,
          page: 1,
          limit: 1,
          roomType: "unassigned",
        })
      );
      if (cancelled || !ListAllRoomsApi.fulfilled.match(result)) return;
      const payload = result.payload || {};
      const roomsArray = Array.isArray(payload.rooms)
        ? payload.rooms
        : Array.isArray(payload?.data?.rooms)
          ? payload.data.rooms
          : [];
      const total =
        payload.pagination?.total ??
        result.payload?.pagination?.total ??
        roomsArray.length;
      if (typeof total === "number") setUnAssignedCount(total);
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, userData?.response?.user?._id, dispatch]);

  useEffect(() => {
    const uid = userData?.response?.user?._id;
    if (!businessId || !uid) return;
    let cancelled = false;
    (async () => {
      const result = await dispatch(
        ListArchivedRoomsApi({
          mainUserId: uid,
          business_id: businessId,
          page: 1,
          limit: 1,
        })
      );
      if (cancelled || !ListArchivedRoomsApi.fulfilled.match(result)) return;
      const payload = result.payload || {};
      const data = payload.data || {};
      const roomsArray = Array.isArray(data.rooms)
        ? data.rooms
        : Array.isArray(payload.rooms)
          ? payload.rooms
          : [];
      const total =
        data.pagination?.total ??
        payload.pagination?.total ??
        roomsArray.length;
      if (typeof total === "number") {
        setClosedCount(total);
        setArchiveChatListWarmCache({
          ...getArchiveChatListWarmCache(),
          businessId,
          total,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [businessId, userData?.response?.user?._id, dispatch, refreshChatList]);

  const refreshUnassignedBadgeTotal = useCallback(async () => {
    const uid = userData?.response?.user?._id;
    if (!businessId || !uid) return;
    const result = await dispatch(
      ListAllRoomsApi({
        mainUserId: uid,
        business_id: businessId,
        page: 1,
        limit: 1,
        roomType: "unassigned",
      })
    );
    if (!ListAllRoomsApi.fulfilled.match(result)) return;
    const payload = result.payload || {};
    const roomsArray = Array.isArray(payload.rooms)
      ? payload.rooms
      : Array.isArray(payload?.data?.rooms)
        ? payload.data.rooms
        : [];
    const total =
      payload.pagination?.total ??
      result.payload?.pagination?.total ??
      roomsArray.length;
    if (typeof total === "number") setUnAssignedCount(total);
  }, [businessId, userData?.response?.user?._id, dispatch]);

  useEffect(() => {
    if (!socket || !businessId || !userData?.response?.user?._id) return;
    const uid = userData.response.user._id;
    const onUnassignedRoomCreated = (room) => {
      if (
        room?.business_id != null &&
        !businessIdsMatch(room.business_id, businessId)
      ) {
        return;
      }

      // Keep the active list and its badge fresh even when another tab is showing,
      // otherwise switching back to All renders a stale list until a hard reload.
      scheduleActiveListRefresh(resolveSocketRoomId(room));

      if (!socketPayloadIsUnassignedChat(room)) return;
      const row = transformSocketRoomToUnassignedListRow(room, uid);
      if (!row) return;
      const wc = getUnassignedChatListWarmCache();
      if (
        wc.hasFetched &&
        businessIdsMatch(wc.businessId, businessId) &&
        Array.isArray(wc.data) &&
        !wc.data.some((r) => r.id === row.id)
      ) {
        const nextTotal =
          typeof wc.total === "number" ? wc.total + 1 : wc.data.length + 1;
        setUnassignedChatListWarmCache({
          businessId: wc.businessId,
          hasFetched: true,
          data: [row, ...wc.data],
          total: nextTotal,
        });
      }
      void refreshUnassignedBadgeTotal();
    };
    socket.on("room-created-notification", onUnassignedRoomCreated);
    return () => {
      socket.off("room-created-notification", onUnassignedRoomCreated);
    };
  }, [
    socket,
    businessId,
    userData?.response?.user?._id,
    refreshUnassignedBadgeTotal,
    scheduleActiveListRefresh,
  ]);

  const getSound = async () => {
    const listCoUserList = dispatch(
      getSoundSetting({
        id: userData?.response?.user?._id,
      })
    );
    listCoUserList
      .then(async (data) => {
        if (data?.payload?.status === 200) {
          const settings = data.payload?.settings;
          console.log("settings", settings?.enableNotifications);
          setSoundOn(settings?.enableNotifications);
        }
      })
      .catch(async (error) => { });
  };

  const handleRoomClicked = (id) => {
    setRmId(id);
  };

  const handleCustomerStatus = useCallback(({ userId, isActive, userType }) => {
    if (userType !== "customer" || userId == null) return;
    const statusUserId = String(userId);
    const nextLive = !!isActive;
    livePresenceRef.current.set(statusUserId, nextLive);
    const matchedRoomIds = [];
    setData((prevData) => {
      let changed = false;
      const next = prevData.map((room) => {
        const matches =
          String(room.customerId) === statusUserId ||
          (Array.isArray(room.roomUsers) &&
            room.roomUsers.some((id) => String(id) === statusUserId));
        if (matches) {
          matchedRoomIds.push(String(room.id));
          if (room.isLive === nextLive) return room;
          changed = true;
          return {
            ...room,
            isLive: nextLive,
            isOnline: isActive ? room.isOnline : false,
          };
        }
        return room;
      });
      return changed ? next : prevData;
    });

    setSelectUser?.((prev) => {
      if (!prev || prev.end_session) return prev;
      const matches =
        String(prev.customerId) === statusUserId ||
        (Array.isArray(prev.roomUsers) &&
          prev.roomUsers.some((id) => String(id) === statusUserId)) ||
        matchedRoomIds.includes(String(prev.id));
      if (!matches || !!prev.isLive === nextLive) return prev;
      return {
        ...prev,
        isLive: nextLive,
        isOnline: isActive ? prev.isOnline : false,
      };
    });
  }, [setSelectUser]);

  /**
   * Sent by the server right after agent setup. `update_user_status` is a
   * broadcast, so a dashboard that loads after a visitor came online would
   * otherwise show them offline until they sent a message.
   */
  const handlePresenceSnapshot = useCallback(
    ({ userType, onlineUserIds } = {}) => {
      if (userType !== "customer" || !Array.isArray(onlineUserIds)) return;
      const onlineIds = new Set(onlineUserIds.map((id) => String(id)));

      const isRoomLive = (room) => {
        if (room?.customerId != null) {
          return onlineIds.has(String(room.customerId));
        }
        return (
          Array.isArray(room?.roomUsers) &&
          room.roomUsers.some((id) => onlineIds.has(String(id)))
        );
      };

      onlineIds.forEach((id) => livePresenceRef.current.set(id, true));

      setData((prevData) => {
        let changed = false;
        const next = prevData.map((room) => {
          if (room?.end_session) return room;
          const nextLive = isRoomLive(room);
          if (room.customerId != null && !nextLive) {
            livePresenceRef.current.set(String(room.customerId), false);
          }
          if (!!room.isLive === nextLive) return room;
          changed = true;
          return {
            ...room,
            isLive: nextLive,
            isOnline: nextLive ? room.isOnline : false,
          };
        });
        return changed ? next : prevData;
      });

      setSelectUser?.((prev) => {
        if (!prev || prev.end_session) return prev;
        const nextLive = isRoomLive(prev);
        if (!!prev.isLive === nextLive) return prev;
        return {
          ...prev,
          isLive: nextLive,
          isOnline: nextLive ? prev.isOnline : false,
        };
      });
    },
    [setSelectUser]
  );

  const handleReceivedMessage = useCallback(
    (newMessageReceived) => {
      console.log(
        "handle Received Message called in ChatBar",
        newMessageReceived
      );

      const userId = userData?.response?.user?._id;
      if (
        soundOnRef.current &&
        userId === newMessageReceived?.message?.assignedTo
      ) {
        // notificationSound.current.play().catch((error) => {
        //   console.error("Error playing sound:", error);
        // });
      }

      console.log("No user selected,", selectUserRef.current);
      console.log("openRoom", openRoom);


      const incomingMessage = newMessageReceived?.message;
      const roomId = incomingMessage?.roomId;
      const newMessageContent = incomingMessage?.content;
      const newFile = incomingMessage?.file_image;
      const isAgent = incomingMessage?.isAgent;
      const latestMessageTime = msgTime(incomingMessage?.createdAt);
      const nextReadReceipt = getChatListReadReceiptFromMessage(incomingMessage);
      const currentRmId = rmIdRef.current;
      const checkTimestamp = new Date();
      const userRole = userData?.response?.user?.role_name;

      // A join notice is room status, not conversation; letting it through would
      // show "… has joined the chat" as the row's last message.
      if (isJoinNoticeMessage(newMessageReceived?.message)) return;

      const roomIsListed = (dataRef.current || []).some(
        (room) => String(room?.id) === String(roomId)
      );
      if (!roomIsListed) {
        // Nothing to update yet, so hold the text for whichever path inserts the
        // row (socket room payload or the refetch below) instead of losing it.
        rememberPendingRoomPreview(roomId, {
          description: newFile ? "Sent Attachment" : newMessageContent,
          attachment: Boolean(newFile),
          iso: newMessageReceived?.message?.createdAt,
        });
        scheduleActiveListRefresh(roomId);
      }

      setData((prevData) => {
        // Find the room that received the new message
        const updatedData = prevData.map((room) => {
          let isOnline = room.isOnline;
          let timeoutId = room.timeoutId;

          if (room.id === roomId) {
            room.iso = checkTimestamp.toISOString();
            if (!isAgent) {
              isOnline = true; // Set the room online when a non-agent message is received
              room.isLive = true;

              // Clear any existing timeout for this room
              if (timeoutId) {
                clearTimeout(room.timeoutId);
              }

              // Set a new timeout to mark the room offline
              timeoutId = setTimeout(() => {
                setData((prevData) => {
                  return prevData.map((r) => {
                    if (r.id === roomId && r.iso === room.iso && !r.isLive) {
                      return {
                        ...r,
                        isOnline: false,
                      };
                    }
                    return r;
                  });
                });

                const roomData =
                  JSON.parse(localStorage.getItem(`room_${roomId}`)) || {};
                if (roomData.iso === room.iso) {
                  roomData.isOnline = false;
                  localStorage.setItem(
                    `room_${roomId}`,
                    JSON.stringify(roomData)
                  );
                }
              }, 90000);
            }
          }

          const roomData = JSON.parse(
            localStorage.getItem(`room_${roomId}`)
          ) || {
            // notification: 0,
            isOnline: false,
          };
          const shouldIncrementCount =
            !isAgent &&
            ((["admin_with_billing", "admin"].includes(userRole) &&
              !room.roomUsers?.includes(userData?.response?.user?._id)) ||
              currentRmId !== roomId ||
              room.is_unAssigned === true);

          if (shouldIncrementCount && room.id === roomId) {
            // Increment the notification count
            // const newCount = roomData.notification + 1;
            const currentCount = (parseInt(room.num, 10) || 0) + 1;
            const unAssignedCount =
              (parseInt(room.unAssignedCount, 10) || 0) + 1;

            // Update room data
            // roomData.notification = newCount;
            roomData.isOnline = isOnline;
            roomData.iso = room.iso;

            localStorage.setItem(`room_${roomId}`, JSON.stringify(roomData));

            return {
              ...room,
              time: latestMessageTime,
              num: currentCount,
              unAssignedCount: unAssignedCount,
              description: newFile ? "Sent Attachment" : newMessageContent,
              attachment: Boolean(newFile),
              isOnline: isOnline,
              isLive: !isAgent ? true : room.isLive,
              timeoutId,
              // Unread badge replaces ticks; otherwise mirror thread read-receipt state.
              readReceipt: currentCount > 0 ? null : nextReadReceipt,
            };
          } else if (room.id === roomId) {
            roomData.isOnline = isOnline;
            roomData.iso = room.iso;

            localStorage.setItem(`room_${roomId}`, JSON.stringify(roomData));
            return {
              ...room,
              time: latestMessageTime,
              description: newFile ? "Sent Attachment" : newMessageContent,
              attachment: Boolean(newFile),
              isOnline: isOnline,
              isLive: !isAgent ? true : room.isLive,
              timeoutId,
              readReceipt: nextReadReceipt,
            };
          }
          return room;
        });
        // Move the room with the new message to the top
        const roomIndex = updatedData.findIndex((room) => room?.id === roomId);
        if (roomIndex !== -1) {
          const [updatedRoom] = updatedData.splice(roomIndex, 1);
          updatedData.unshift(updatedRoom);
        }

        return updatedData;
      });

      if (!isAgent) {
        setSelectUser?.((prev) => {
          if (!prev || String(prev.id) !== String(roomId) || prev.end_session) {
            return prev;
          }
          if (prev.isLive) return prev;
          return { ...prev, isLive: true, isOnline: true };
        });
      }
    },
    [setData, userData?.response?.user?._id, scheduleActiveListRefresh, setSelectUser]
  );

  /** A chat added to the list by a socket event has to move the All badge too. */
  const handleActiveRoomAdded = useCallback(
    (roomId) => {
      setAssignedCount((c) => (c || 0) + 1);
      scheduleActiveListRefresh(roomId);
    },
    [scheduleActiveListRefresh]
  );

  /**
   * The opened thread can hold a message the room list API never reported (the
   * widget welcome on a fresh chat), which would leave the row's preview blank.
   */
  useEffect(() => {
    const onPreviewResolved = (event) => {
      const { roomId, description, attachment } = event?.detail || {};
      if (!roomId) return;

      setData((prevData) => {
        let changed = false;
        const next = (prevData || []).map((room) => {
          if (String(room?.id) !== String(roomId)) return room;
          const alreadyHasPreview =
            Boolean(String(room?.description || "").trim()) || Boolean(room?.attachment);
          if (alreadyHasPreview) return room;
          changed = true;
          return {
            ...room,
            description: description || "",
            attachment: Boolean(attachment),
          };
        });
        return changed ? next : prevData;
      });
    };

    window.addEventListener(ROOM_PREVIEW_RESOLVED_EVENT, onPreviewResolved);
    return () =>
      window.removeEventListener(ROOM_PREVIEW_RESOLVED_EVENT, onPreviewResolved);
  }, [setData]);

  const handleChatAssign = (assignedChat) => {
    // Optimistically add to chat list at the top
    setData((prevData) => [assignedChat, ...prevData]);

    // Switch to Chats view
    setActiveChat("Chats");
  };

  /**
   * Moves a room out of Unassigned after Start Chat or assign-from-unassigned.
   * When `claimForSelf` is true (default), also bumps My Chats and refreshes All.
   */
  const markRoomAssigned = useCallback(
    (roomIdRaw, { claimForSelf = true } = {}) => {
      const roomId = roomIdRaw == null ? "" : String(roomIdRaw);
      if (!roomId) return;
      if (countedAssignedFromUnassignedRef.current.has(roomId)) return;
      countedAssignedFromUnassignedRef.current.add(roomId);

      const result = removeRoomFromUnassignedWarmCache(roomId);
      if (result.removed && typeof result.total === "number") {
        setUnAssignedCount(result.total);
      } else {
        setUnAssignedCount((c) => Math.max(0, (c || 0) - 1));
      }

      if (!claimForSelf) return;

      setAssignedCount((c) => (c || 0) + 1);

      const wc = getActiveChatListWarmCache();
      if (businessIdsMatch(wc.businessId, businessId)) {
        setActiveChatListWarmCache({
          ...wc,
          assignedCount: Math.max(
            0,
            (typeof wc.assignedCount === "number"
              ? wc.assignedCount
              : Array.isArray(wc.data)
                ? wc.data.length
                : 0) + 1
          ),
        });
      }

      scheduleActiveListRefresh(roomId);
    },
    [businessId, scheduleActiveListRefresh]
  );

  const transformRooms = useCallback((rooms) => {
    const loggedInAgentId = userData?.response?.user?._id;
    return rooms.map((item) => {
      const date = new Date(item.createdAt);
      const year = date.getFullYear();
      const month = `0${date.getMonth() + 1}`.slice(-2);
      const day = `0${date.getDate()}`.slice(-2);
      const hours = `0${date.getHours()}`.slice(-2);
      const minutes = `0${date.getMinutes()}`.slice(-2);
      const amOrPm = hours >= 12 ? "PM" : "AM";
      const formattedHours = hours % 12 || 12;
      const formattedTime = `${year}-${month}-${day} ${formattedHours}:${minutes} ${amOrPm}`;

      const roomData = JSON.parse(
        localStorage.getItem(`room_${item._id}`)
      ) || { notification: 0, isOnline: false };
      const activityOnline = !!roomData.isOnline;

      const users = Array.isArray(item?.users) ? item.users : [];
      const customerUser =
        users.find((u) => u?._id && u?._id !== loggedInAgentId && u?.is_guest) ||
        users.find((u) => u?._id && u?._id !== loggedInAgentId) ||
        users[0] ||
        null;

      const knownLive =
        customerUser?._id != null
          ? livePresenceRef.current.get(String(customerUser._id))
          : undefined;
      const socketActive =
        typeof knownLive === "boolean"
          ? knownLive
          : customerUser
            ? !!customerUser?.socket_active
            : false;

      const roomId = item._id || item.id;
      const latestMessage = item?.latestMessageSnapshot || item?.latestMessage;
      const apiDescription = latestMessage?.content;
      // A just-started chat can be refetched before its first message is attached
      // to the room, so keep the socket-delivered preview over a blank row.
      const pendingPreview = apiDescription ? null : getPendingRoomPreview(roomId);
      const unreadCount = item?.unreadMessages?.[0]?.count || 0;

      return {
        id: roomId,
        name: userData?.response?.user?.full_name,
        title: item?.roomName,
        logo: <Jacob />,
        description: apiDescription || pendingPreview?.description || "",
        attachment:
          latestMessage?.file_image ||
          pendingPreview?.attachment ||
          false,
        time: msgTime(
          latestMessage?.createdAt ||
            pendingPreview?.iso
        ),
        startTime: formattedTime,
        text: "Windows",
        deviceType: customerUser?.deviceType,
        os: customerUser?.os,
        McLogo: <Laptop />,
        is_unAssigned: item?.is_unAssigned,
        is_archived: item?.is_archived,
        end_session: item?.end_session,
        archived_message: item?.archived_message || item?.endedMessage,
        num: unreadCount,
        unAssignedCount: item?.unAssignedMessageCount || 0,
        iso:
          latestMessage?.createdAt ||
          pendingPreview?.iso,
        // Match ChatMessageComponent: isOnline (recent visitor activity from LS) ?? isLive (API socket_active)
        isOnline: activityOnline,
        isLive: socketActive,
        customerId: customerUser?._id || null,
        roomUsers: users.map((user) => user._id),
        businessId: item?.business_id,
        assignedTo: item?.assigned_to,
        tags: item?.tags || [],
        is_guest: customerUser?.is_guest || false,
        readReceipt: getChatListReadReceiptFromRoom(item, unreadCount),
      };
    });
  }, [userData]);

  /** Closed chats belong to the Closed tab only, even if a refetch still lists them. */
  const excludeClosedRooms = useCallback(
    (rows) =>
      rows.filter(
        (room) =>
          !room?.is_archived && !countedClosedRoomsRef.current.has(String(room?.id))
      ),
    []
  );

  const getRoomList = useCallback(async (pageNum = 1, options = {}) => {
    const { background = false } = options;
    console.log("get room list called, page:", pageNum, background ? "(background)" : "");
    if (pageNum === 1) {
      if (!background) {
        setIsLoading(true);
      }
    } else {
      isFetchingMoreRef.current = true;
      setIsFetchingMore(true);
    }

    try {
      const result = await dispatch(
        ListAllRoomsApi({
          mainUserId: userData?.response?.user?._id,
          business_id: userData?.response?.user?.business_id,
          page: pageNum,
          limit: 10,
          roomType: 'active',
        })
      );

      if (!result.payload) {
        toast.error("Some Error Occurred. Please try again later.");
        setIsLoading(false);
        isFetchingMoreRef.current = false;
        setIsFetchingMore(false);
        return;
      }
      if (!result.payload?.rooms || !Array.isArray(result.payload.rooms)) {
        setIsLoading(false);
        isFetchingMoreRef.current = false;
        setIsFetchingMore(false);
        return;
      }

      if (result.payload.success === true) {
        const rooms = result.payload.rooms;
        const transformed = transformRooms(rooms);
        const outputarray = excludeClosedRooms(transformed);
        // Rooms closed locally are dropped from the list before the server total
        // catches up, so discount them or the tab reads higher than the list.
        const locallyClosed = transformed.length - outputarray.length;

        let nextHasMore = rooms.length === 10;
        if (result.payload.pagination) {
          const { page: p, totalPages } = result.payload.pagination;
          nextHasMore = p < totalPages;
        }
        setHasMore(nextHasMore);

        if (pageNum === 1) {
          const reportedTotal = result.payload.pagination?.total ?? rooms.length;
          const total = Math.max(0, reportedTotal - locallyClosed);
          setAssignedCount(total);
          setData((prev) => {
            const prevById = new Map(prev.map((r) => [r.id, r]));
            const merged = outputarray.map((room) =>
              mergeVisitorOnlineState(room, prevById.get(room.id))
            );
            const bid = userData?.response?.user?.business_id;
            if (bid) {
              setActiveChatListWarmCache({
                businessId: bid,
                data: merged,
                hasFetched: true,
                assignedCount: total,
                hasMore: nextHasMore,
              });
            }
            return merged;
          });
        } else {
          setData((prev) => {
            const existingIds = new Set(prev.map((r) => r.id));
            const newRooms = outputarray.filter((r) => !existingIds.has(r.id));
            return [...prev, ...newRooms];
          });
        }

        if (socket && rooms.length) {
          rooms.forEach((room) => {
            socket.emit("join chat", room._id);
          });
        }

        setIsLoading(false);
        isFetchingMoreRef.current = false;
        setIsFetchingMore(false);
      } else {
        setIsLoading(false);
        isFetchingMoreRef.current = false;
        setIsFetchingMore(false);
      }
    } catch (error) {
      // Keep the last known count: the list is not cleared here either, so
      // zeroing it would show an empty badge over a populated list.
      setIsLoading(false);
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
      toast.error(error);
    }
  }, [dispatch, userData, socket, transformRooms, excludeClosedRooms]);

  getRoomListRef.current = getRoomList;

  const searchRooms = useCallback(
    async (pageNum = 1, query = trimmedSearch) => {
      const term = String(query || "").trim();
      if (!term) return;

      const requestId = ++searchRequestIdRef.current;
      if (pageNum === 1) {
        setSearchLoading(true);
      } else {
        searchFetchingMoreRef.current = true;
        setSearchFetchingMore(true);
      }

      try {
        const result = await dispatch(
          SearchChatRoomsApi({
            search: term,
            page: pageNum,
            limit: 10,
          })
        );

        if (requestId !== searchRequestIdRef.current) return;

        if (!result.payload) {
          toast.error("Some Error Occurred. Please try again later.");
          setSearchLoading(false);
          searchFetchingMoreRef.current = false;
          setSearchFetchingMore(false);
          return;
        }

        const rooms = Array.isArray(result.payload?.rooms)
          ? result.payload.rooms
          : Array.isArray(result.payload?.data)
            ? result.payload.data
            : [];

        if (result.payload?.success === false) {
          setSearchLoading(false);
          searchFetchingMoreRef.current = false;
          setSearchFetchingMore(false);
          return;
        }

        const transformed = transformRooms(rooms);

        let nextHasMore = rooms.length === 10;
        if (result.payload?.pagination) {
          const { page: p, totalPages } = result.payload.pagination;
          nextHasMore = p < totalPages;
        }
        setSearchHasMore(nextHasMore);
        searchHasMoreRef.current = nextHasMore;
        setSearchPage(pageNum);
        searchPageRef.current = pageNum;

        if (pageNum === 1) {
          setSearchData(transformed);
        } else {
          setSearchData((prev) => {
            const existingIds = new Set(prev.map((r) => r.id));
            const newRooms = transformed.filter((r) => !existingIds.has(r.id));
            return [...prev, ...newRooms];
          });
        }

        if (socket && rooms.length) {
          rooms.forEach((room) => {
            if (room?._id) socket.emit("join chat", room._id);
          });
        }
      } catch (error) {
        if (requestId === searchRequestIdRef.current) {
          toast.error(error?.message || "Search failed");
        }
      } finally {
        if (requestId === searchRequestIdRef.current) {
          setSearchLoading(false);
          searchFetchingMoreRef.current = false;
          setSearchFetchingMore(false);
        }
      }
    },
    [dispatch, trimmedSearch, transformRooms, socket]
  );

  useEffect(() => {
    if (!isSearching) {
      searchRequestIdRef.current += 1;
      setSearchData([]);
      setSearchLoading(false);
      setSearchPage(1);
      searchPageRef.current = 1;
      setSearchHasMore(false);
      searchHasMoreRef.current = false;
      searchFetchingMoreRef.current = false;
      setSearchFetchingMore(false);
      return;
    }
    setSearchPage(1);
    searchPageRef.current = 1;
    void searchRooms(1, trimmedSearch);
  }, [isSearching, trimmedSearch, searchRooms]);

  const loadMoreSearch = useCallback(() => {
    if (searchFetchingMoreRef.current || !searchHasMoreRef.current) return;
    const nextPage = searchPageRef.current + 1;
    void searchRooms(nextPage, trimmedSearch);
  }, [searchRooms, trimmedSearch]);

  useLayoutEffect(() => {
    if (!businessId) return;
    const wc = getActiveChatListWarmCache();
    if (
      !wc.hasFetched ||
      !businessIdsMatch(wc.businessId, businessId) ||
      !Array.isArray(wc.data)
    ) {
      return;
    }
    setData(wc.data);
    setAssignedCount(wc.assignedCount ?? wc.data.length);
    if (typeof wc.hasMore === "boolean") setHasMore(wc.hasMore);
    setIsLoading(false);
  }, [businessId]);

  useEffect(() => {
    if (!socket) return;

    emitAgentSetup(socket);

    const onConnected = () => {
      console.log("Socket Connected ChatBar");
    };

    const onTyping = (r) => {
      if (r?._id) {
        setTypingRooms((prev) => ({ ...prev, [r._id]: true }));
      }
    };
    const onNotTyping = (r) => {
      if (r?._id) {
        setTypingRooms((prev) => ({ ...prev, [r._id]: false }));
      }
    };

    socket.on("connected", onConnected);
    socket.on("msg fetched", handleReceivedMessage);
    socket.on("update_user_status", handleCustomerStatus);
    socket.on("presence_snapshot", handlePresenceSnapshot);
    socket.on("is-typing", onTyping);
    socket.on("not-typing", onNotTyping);

    return () => {
      socket.off("connected", onConnected);
      socket.off("msg fetched", handleReceivedMessage);
      socket.off("update_user_status", handleCustomerStatus);
      socket.off("presence_snapshot", handlePresenceSnapshot);
      socket.off("is-typing", onTyping);
      socket.off("not-typing", onNotTyping);
    };
  }, [
    userData?.response?.user?._id,
    socket,
    handleReceivedMessage,
    handleCustomerStatus,
    handlePresenceSnapshot,
  ]);

  /**
   * Moves a room out of the active/unassigned lists and into Closed. Safe to call
   * more than once for the same room: the local close and the socket echo both
   * land here. If that room is open, keep the thread painted as closed/archived
   * but do not leave a ghost row on All (that skewed counts and sort order).
   */
  const markRoomClosed = useCallback(
    (roomIdRaw) => {
      const roomId = roomIdRaw == null ? "" : String(roomIdRaw);
      if (!roomId) return;

      // A single close arrives several times (the local close plus the
      // archive-chat / end-room / end-chat echoes). Dedupe before touching any
      // counter: `dataRef` is still pre-filter within the same tick, so a second
      // pass would decrement My Chats again and show one less than the list.
      if (countedClosedRoomsRef.current.has(roomId)) return;
      countedClosedRoomsRef.current.add(roomId);

      const matchesRoom = (room) => String(room?.id) === roomId;
      const closedRow = (dataRef.current || []).find(matchesRoom) || null;
      const wasInActiveList = !!closedRow;

      if (wasInActiveList) {
        setData((prev) => prev.filter((room) => !matchesRoom(room)));
        setAssignedCount((c) => Math.max(0, (c || 0) - 1));
      }

      // Open thread can stay on screen with the archive footer; the All list
      // must not keep the row.
      setSelectUser?.((prev) => {
        if (!prev || String(prev.id) !== roomId) return prev;
        return {
          ...prev,
          end_session: true,
          endSession: true,
          endSessionTime: prev.endSessionTime || new Date().toISOString(),
          is_archived: true,
          isOnline: false,
          isLive: false,
        };
      });

      const wc = getActiveChatListWarmCache();
      if (
        Array.isArray(wc.data) &&
        businessIdsMatch(wc.businessId, businessId) &&
        wc.data.some(matchesRoom)
      ) {
        setActiveChatListWarmCache({
          ...wc,
          data: wc.data.filter((room) => !matchesRoom(room)),
          assignedCount: Math.max(
            0,
            (typeof wc.assignedCount === "number"
              ? wc.assignedCount
              : wc.data.length) - 1
          ),
        });
      }

      // Drop from unassigned warm cache if present (no remount / skeleton)
      const unWc = getUnassignedChatListWarmCache();
      const unassignedRow = Array.isArray(unWc.data)
        ? unWc.data.find(matchesRoom) || null
        : null;
      const wasUnassigned = !!unassignedRow;
      if (wasUnassigned) {
        const nextUn = unWc.data.filter((room) => !matchesRoom(room));
        const nextTotal =
          typeof unWc.total === "number"
            ? Math.max(0, unWc.total - 1)
            : nextUn.length;
        setUnassignedChatListWarmCache({
          ...unWc,
          data: nextUn,
          total: nextTotal,
        });
        setUnAssignedCount(nextTotal);
      }

      setClosedCount((c) => (c || 0) + 1);
      const archiveWc = getArchiveChatListWarmCache();
      setArchiveChatListWarmCache({
        ...archiveWc,
        businessId: businessId || archiveWc.businessId,
        total:
          typeof archiveWc.total === "number"
            ? archiveWc.total + 1
            : archiveWc.total,
        data: null,
        hasMore: true,
        page: 1,
      });
      // Tells a mounted Closed list to pull the room in without a manual refresh.
      // The row travels with it so Closed can show the chat even before the
      // archived-rooms endpoint reports it.
      setClosedRoomSignal({
        roomId,
        row: closedRow || unassignedRow,
        at: Date.now(),
      });
    },
    [businessId, setSelectUser]
  );

  useEffect(() => {
    if (!removeRoomId) return;
    markRoomClosed(removeRoomId);
    // Agent ended the chat themselves: list/counts update here, but stay on the
    // current tab (do not jump to Closed).
    onRemoveRoomConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeRoomId, markRoomClosed, onRemoveRoomConsumed]);

  useEffect(() => {
    if (!assignedRoomId) return;
    markRoomAssigned(assignedRoomId, {
      claimForSelf: assignedRoomClaimForSelf !== false,
    });
    onAssignedRoomConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedRoomId, markRoomAssigned, onAssignedRoomConsumed, assignedRoomClaimForSelf]);

  // A chat can also be closed outside this tab: another agent or admin closing it,
  // or the visitor ending the chat from the widget.
  useEffect(() => {
    if (!socket) return;

    const onChatClosed = (payload) => {
      const roomId = resolveSocketRoomId(payload);
      if (!roomId) return;
      // Drop from All / Unassigned and bump Closed. Stay on the current tab —
      // never jump to Closed. If this thread is open, markRoomClosed paints it
      // as archived in the chat area.
      markRoomClosed(roomId);
    };

    CHAT_CLOSED_SOCKET_EVENTS.forEach((event) => socket.on(event, onChatClosed));
    return () => {
      CHAT_CLOSED_SOCKET_EVENTS.forEach((event) =>
        socket.off(event, onChatClosed)
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, markRoomClosed]);

  useEffect(() => {
    const toggled = prevRefreshChatListRef.current !== refreshChatList;
    prevRefreshChatListRef.current = refreshChatList;
    const wc = getActiveChatListWarmCache();
    const warm =
      Boolean(businessId) &&
      wc.hasFetched &&
      businessIdsMatch(wc.businessId, businessId) &&
      Array.isArray(wc.data);
    const run = getRoomListRef.current;
    if (!run) return;
    setPage(1);
    setHasMore(true);
    if (!toggled && warm) {
      void run(1, { background: true });
    } else {
      void run(1, { background: false });
    }
    getSound();
  }, [refreshChatList, businessId]);

  const loadMore = useCallback(() => {
    if (isFetchingMoreRef.current || !hasMoreRef.current) return;
    const nextPage = pageRef.current + 1;
    setPage(nextPage);
    getRoomList(nextPage);
  }, [getRoomList]);

  return (
    <div className={`ChatBar-Main-Container${variant === "v2" ? " chatv2-chatbar" : ""}`}>
      <div className="ChatBar-Container01">
        {variant !== "v2" && (
          <>
        <p className="Chatbar-Heading">Chats</p>
        <div className="ChatBar-Heading-Container">
          <p
            style={{ paddingBottom: '8px' }}
            className={`Archive-text ${activeChat === "Chats" ? "activeChat" : ""
              }`}
            onClick={() => {
              handleViewChange("Chats");
              setSelectUser(null);
              setChatSideBar(false);
            }}
          >
            My Chats ({assignedCount})
          </p>
          <p
            style={{ paddingBottom: '8px' }}
            className={`Archive-text ${activeChat === "UnAssigned" ? "activeChat" : ""
              }`}
            onClick={() => {
              handleViewChange("UnAssigned");
              setChatSideBar(false);
              setSelectUser(null);
            }}
          >
            Unassigned ({unAssignedCount})
          </p>
          <p
            style={{ paddingBottom: '8px' }}
            className={`Archive-text ${activeChat === "Archive" ? "activeChat" : ""
              }`}
            onClick={() => {
              handleViewChange("Archive");
              setChatSideBar(false);
              setSelectUser(null);
            }}
          >
            Archive
          </p>
        </div>
          </>
        )}

        {isSearching ? (
          searchLoading ? (
            <ChatListSkeleton />
          ) : (
            <ChatUser
              data={filterListForSearchOpen(searchData)}
              fromSearch={fromSearch}
              urlRoomId={urlRoomId}
              roomClicked={handleRoomClicked}
              soundOn={soundOn}
              setData={setSearchData}
              getRoomList={(pageNum = 1) => searchRooms(pageNum, trimmedSearch)}
              setLatestMsgTime={setLatestMsgTime}
              selectUser={selectUser}
              setSelectUser={setSelectUser}
              setGetRoomId={setGetRoomId}
              setAssignedAgentImage={setAssignedAgentImage}
              getRoomId={getRoomId}
              refreshChatList={refreshChatList}
              openRoom={openRoom}
              typingRooms={typingRooms}
              setTypingIndicator={setTypingIndicator}
              setChatSideBar={setChatSideBar}
              hasMore={searchHasMore}
              isFetchingMore={searchFetchingMore}
              loadMore={loadMoreSearch}
              onRoomAdded={handleActiveRoomAdded}
              variant={variant}
              emptyMessage="No chats found"
            />
          )
        ) : listView === "Chats" ? (
          isLoading ? (
            <ChatListSkeleton />
          ) : (
            <ChatUser
              data={filterListForSearchOpen(data)}
              fromSearch={fromSearch}
              urlRoomId={urlRoomId}
              roomClicked={handleRoomClicked}
              soundOn={soundOn}
              setData={setData}
              getRoomList={getRoomList}
              setLatestMsgTime={setLatestMsgTime}
              selectUser={selectUser}
              setSelectUser={setSelectUser}
              setGetRoomId={setGetRoomId}
              setAssignedAgentImage={setAssignedAgentImage}
              getRoomId={getRoomId}
              refreshChatList={refreshChatList}
              openRoom={openRoom}
              typingRooms={typingRooms}
              setTypingIndicator={setTypingIndicator}
              setChatSideBar={setChatSideBar}
              hasMore={hasMore}
              isFetchingMore={isFetchingMore}
              loadMore={loadMore}
              onRoomAdded={handleActiveRoomAdded}
              variant={variant}
            />
          )
        ) : listView === "UnAssigned" ? (
          <UnAssignedRooms
            fromSearch={fromSearch}
            urlRoomId={urlRoomId}
            roomClicked={handleRoomClicked}
            setSelectUser={setSelectUser}
            setGetRoomId={setGetRoomId}
            getRoomId={getRoomId}
            selectUser={selectUser}
            refreshChatList={refreshChatList}
            setChatSideBar={setChatSideBar}
            setUnAssignedCount={setUnAssignedCount}
            removeRoomId={removeRoomId}
            assignedRoomId={assignedRoomId}
            variant={variant}
          />
        ) : (
          <ArchiveUser
            fromSearch={fromSearch}
            urlRoomId={urlRoomId}
            selectUser={selectUser}
            setSelectUser={setSelectUser}
            setGetRoomId={setGetRoomId}
            getRoomId={getRoomId}
            refreshChatList={refreshChatList}
            setChatSideBar={setChatSideBar}
            setRoomMessagesLoading={setRoomMessagesLoading}
            setClosedCount={setClosedCount}
            closedRoomSignal={closedRoomSignal}
            variant={variant}
          />
        )}
      </div>
    </div>
  );
}

export default ChatBar;
